import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  getLogRocketHighlights,
  checkLogRocketHighlightsStatus,
  LogRocketHighlightsResponse,
  LogRocketSession,
} from '../api/dashboard';

// Better contrast purple for links (works on both light and dark backgrounds)
const LINK_COLOR = '#B794F4';

interface LogRocketHighlightsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  timestamp: string | null;
  userName?: string | null;
}

export default function LogRocketHighlightsModal({
  open,
  onClose,
  userId,
  timestamp,
  userName,
}: LogRocketHighlightsModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LogRocketHighlightsResponse | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (open && userId && timestamp) {
      fetchHighlights();
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [open, userId, timestamp]);

  const fetchHighlights = async () => {
    if (!userId || !timestamp) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      // Ensure timestamp is treated as UTC if no timezone specified
      const normalizedTimestamp = timestamp.endsWith('Z') || timestamp.includes('+')
        ? timestamp
        : timestamp + 'Z';
      const timestampMs = new Date(normalizedTimestamp).getTime();
      const response = await getLogRocketHighlights(userId, timestampMs);

      if (response.status === 'ready') {
        setData(response);
        setLoading(false);
      } else if (response.status === 'pending' && response.requestId) {
        // Start polling
        pollForResults(response.requestId);
      } else if (response.status === 'disabled') {
        setError('LogRocket is not enabled for this environment');
        setLoading(false);
      } else {
        setError(response.error || 'Failed to fetch highlights');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to fetch highlights');
      setLoading(false);
    }
  };

  const pollForResults = (requestId: string) => {
    let attempts = 0;
    const maxAttempts = 60; // 2 minutes max (2s intervals)

    pollIntervalRef.current = setInterval(async () => {
      attempts++;

      if (attempts >= maxAttempts) {
        clearInterval(pollIntervalRef.current!);
        setError('LogRocket is still generating the summary. Please close this dialog and try again in 1-2 minutes.');
        setLoading(false);
        return;
      }

      try {
        const response = await checkLogRocketHighlightsStatus(requestId);

        if (response.status === 'ready') {
          clearInterval(pollIntervalRef.current!);
          setData(response);
          setLoading(false);
        } else if (response.status === 'failed') {
          clearInterval(pollIntervalRef.current!);
          setError(response.error || 'Failed to generate highlights');
          setLoading(false);
        }
      } catch {
        // Continue polling on error
      }
    }, 2000);
  };

  const handleClose = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    setData(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  const renderMarkdownLinks = (text: string) => {
    // Convert markdown links [text](url) to clickable links
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Add the link
      parts.push(
        <Link
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: LINK_COLOR, '&:hover': { color: '#D6BCFA' } }}
        >
          {match[1]}
          <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25, verticalAlign: 'middle' }} />
        </Link>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src="/logrocket-logo.png"
              alt="LogRocket"
              width={20}
              height={20}
              style={{ borderRadius: 3 }}
            />
            <Typography variant="h6">Session Highlights</Typography>
          </Box>
          {userName && (
            <Typography variant="caption" color="text.secondary">
              {userName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={40} sx={{ color: LINK_COLOR }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generating session highlights...
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              This may take up to a minute
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && data.status === 'ready' && (
          <Box>
            {/* Overall Summary */}
            {data.overallHighlights && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Summary
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.7,
                    bgcolor: 'rgba(183, 148, 244, 0.08)',
                    p: 2,
                    borderRadius: 1,
                    borderLeft: '3px solid',
                    borderLeftColor: LINK_COLOR,
                  }}
                >
                  {renderMarkdownLinks(data.overallHighlights)}
                </Typography>
              </Box>
            )}

            {/* Sessions */}
            {data.sessions && data.sessions.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Sessions ({data.sessions.length})
                </Typography>

                {data.sessions.map((session, index) => (
                  <SessionCard key={session.recordingId} session={session} index={index} />
                ))}
              </Box>
            )}

            {(!data.sessions || data.sessions.length === 0) && !data.overallHighlights && (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No session data found for this time period.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ScreenshotPreview {
  url: string;
  logRocketUrl: string;
  index: number;
}

function SessionCard({ session, index }: { session: LogRocketSession; index: number }) {
  const [previewScreenshot, setPreviewScreenshot] = useState<ScreenshotPreview | null>(null);

  const formatTime = (timestampMs: number) => {
    return new Date(timestampMs).toLocaleString();
  };

  const renderMarkdownLinks = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      parts.push(
        <Link
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: LINK_COLOR, '&:hover': { color: '#D6BCFA' } }}
        >
          {match[1]}
          <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25, verticalAlign: 'middle' }} />
        </Link>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const handleScreenshotClick = (screenshot: { url: string; videoTime: number }, i: number) => {
    setPreviewScreenshot({
      url: screenshot.url,
      logRocketUrl: `${session.sessionUrl}?t=${screenshot.videoTime}`,
      index: i,
    });
  };

  return (
    <>
      <Accordion
        defaultExpanded={index === 0}
        sx={{
          mb: 1,
          '&:before': { display: 'none' },
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px !important',
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Chip
              label={`Session ${index + 1}`}
              size="small"
              sx={{
                bgcolor: 'rgba(183, 148, 244, 0.15)',
                color: LINK_COLOR,
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {formatTime(session.minVideoTime)} - {formatTime(session.maxVideoTime)}
            </Typography>
            <Link
              href={session.sessionUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, color: LINK_COLOR }}
            >
              <Typography variant="caption">View Full Session</Typography>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>
            {renderMarkdownLinks(session.highlights)}
          </Typography>

          {/* Screenshots */}
          {session.screenshots && session.screenshots.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Screenshots (click to enlarge)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {session.screenshots.slice(0, 5).map((screenshot, i) => (
                  <Box
                    key={i}
                    onClick={() => handleScreenshotClick(screenshot, i)}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover .zoom-overlay': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={screenshot.url}
                      alt={`Screenshot ${i + 1}`}
                      sx={{
                        width: 120,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 2,
                        },
                      }}
                    />
                    <Box
                      className="zoom-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        borderRadius: 1,
                        opacity: 0,
                        transition: 'opacity 0.15s',
                      }}
                    >
                      <ZoomInIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Screenshot Preview Dialog */}
      <Dialog
        open={!!previewScreenshot}
        onClose={() => setPreviewScreenshot(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">
            Screenshot {previewScreenshot ? previewScreenshot.index + 1 : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {previewScreenshot && (
              <Link
                href={previewScreenshot.logRocketUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: LINK_COLOR,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
                Open in LogRocket
              </Link>
            )}
            <IconButton onClick={() => setPreviewScreenshot(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {previewScreenshot && (
            <Box
              component="img"
              src={previewScreenshot.url}
              alt="Screenshot preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
